import React from "react";
import { T } from "../../../app/tokens.js";
import { parseNumericField } from "../../../utils/parseNumericField.js";
import { LeaseAreaRentEditor } from "./LeaseAreaRentEditor.jsx";
import { leaseAreaPresentation } from "../leaseAreaPresentation.js";
import { AreaLifecycleBadge } from "./AreaLifecycleBadge.jsx";

const normalizedDecimal = (text) => {
  const [whole, fraction = ""] = text.split(".");
  return `${whole.replace(/^0+/, "") || "0"}.${fraction.replace(/0+$/, "")}`;
};

export function parseAreaFootage(value) {
  const parsed = parseNumericField(value);
  if (String(value ?? "").trim() === "") return { value: null, error: null };
  // Reject hexadecimal/exponent/malformed separators rather than Number coercion.
  const validText = /^(?:(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?|\.\d+)$/.test(String(value).trim());
  if (!validText || parsed == null || parsed < 0 || parsed > 1000000000) {
    return { value: null, error: "Enter a number from 0 to 1,000,000,000, or leave blank." };
  }
  // Preserve decimal intent: reject inputs whose Number/JSON round-trip loses
  // digits rather than quietly saving a different value.
  const [mantissa, exponent] = String(parsed).split("e");
  const plain = exponent ? `0.${"0".repeat(-Number(exponent) - 1)}${mantissa.replace(".", "")}` : mantissa;
  if (normalizedDecimal(String(value).trim().replace(/,/g, "")) !== normalizedDecimal(plain)) {
    return { value: null, error: "Use a value that can be saved without rounding." };
  }
  return { value: parsed, error: null };
}

const FIELDS = [{ key: "sizeSf", label: "Est. Sqft" }, { key: "usableSizeSf", label: "Est. Usable Sqft" }];

export function LeaseAreaEditor({ areas, drafts, onChange, disabled, currency, asOf = new Date() }) {
  const { groups } = leaseAreaPresentation({ areas }, asOf);
  return <div data-testid="lease-area-editor">
    <div style={{ fontSize: T.fsXs, color: T.mid }}>
      Each area uses its own recorded footage, separate from lease Size (sf). Leave blank to keep the stored value unchanged.
    </div>
    {groups.map((group) => <React.Fragment key={group.status}>
      <div style={{ color: T.light, fontSize: T.fsXs, fontWeight: T.fwSemibold, marginTop: 12 }}>
        {group.status === "unknown" ? group.label : `${group.label} Areas`}
      </div>
    {group.areas.map((area) => <div key={area.id} style={{ padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
      <div style={{ color: area.status === "expired" ? T.light : T.dark, fontWeight: T.fwSemibold, marginBottom: 6 }}>
        {area.name}<AreaLifecycleBadge area={area} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {FIELDS.map(({ key, label }) => {
          const value = drafts[area.id]?.[key] ?? String(area[key] ?? "");
          const error = parseAreaFootage(value).error;
          const id = `area-${area.id}-${key}`;
          return <div key={key}>
            <label htmlFor={id} style={{ fontSize: T.fsSm, color: T.mid }}>{label}</label>
            <input id={id} aria-label={`${area.name} ${label}`} type="text" inputMode="decimal"
              value={value} disabled={disabled} aria-invalid={!!error}
              aria-describedby={error ? `${id}-error` : undefined}
              onChange={(event) => onChange(area.id, key, event.target.value)}
              style={{ width: "100%", boxSizing: "border-box", padding: "5px 8px", borderRadius: 4,
                border: `1px solid ${error ? T.red : T.borderDark}`, background: T.white, color: T.dark,
                fontSize: T.fsBase, fontFamily: "inherit" }} />
            {error && <div id={`${id}-error`} role="alert" style={{ fontSize: T.fsXs, color: T.red }}>{error}</div>}
          </div>;
        })}
      </div>
      <LeaseAreaRentEditor area={area} drafts={drafts[area.id]?.rentRates}
        onChange={onChange} disabled={disabled} currency={currency} />
    </div>)}
    </React.Fragment>)}
  </div>;
}
